/**
 * 도입 상담(체험 계정) 신청 폼의 공통 규칙·전송.
 *
 * 랜딩의 `LandingApplyModal` 이 쓴다. 논서술형 완료 화면의 도입 문의(`EssayAdoptionInquiryModal`)는
 * 항목이 달라(`essayAdoptionInquiryForm.ts`) 여기서는 전화번호 규칙·지역 목록·기본 안내 문구·
 * 전송(`submitApply`)만 같이 쓴다. 두 폼 모두 같은 Lambda 엔드포인트로 보내고 `source` 로 구분한다.
 *
 * ★ Lambda(레포 codehana-official/lambda 의 `dq-landing-apply/index.mjs`)가 받는 필드가 곧
 *   `ApplyPayload`(랜딩) / `EssayAdoptionInquiryPayload`(도입 문의)다. 필드를 더하거나 이름을 바꾸면
 *   Lambda 도 같이 고치고 dev·prod 스택을 다시 배포해야 한다.
 */
import { getLandingFormEndpoint } from "./config";

export const PHONE = /^0\d{1,2}-?\d{3,4}-?\d{4}$/;

export const REGIONS = [
  "서울", "부산", "대구", "인천", "광주", "대전", "울산", "세종", "경기",
  "강원", "충북", "충남", "전북", "전남", "경북", "경남", "제주",
];

export const SCHOOL_TYPES = ["초등학교", "중학교", "고등학교"];

/**
 * 학교 구분에 따른 학년 선택 범위 `[최소, 최대]`.
 * 초등은 3~6학년만 받는다. 1·2학년은 검사 대상이 아니다.
 * 학교 구분을 아직 안 골랐으면 `[0, 0]` 이라 학년 select 가 비활성된다.
 */
export const gradeRange = (schoolType: string): [number, number] =>
  schoolType === "초등학교" ? [3, 6] : schoolType ? [1, 3] : [0, 0];

export const DEFAULT_APPLY_NOTE = "영업일 기준 3일 이내에 담당자가 연락드립니다.";

export type ApplyForm = {
  region: string;
  name: string;
  email: string;
  phone: string;
  schoolType: string;
  grade: string;
  message: string;
  consent: boolean;
};

export const EMPTY_APPLY_FORM: ApplyForm = {
  region: "",
  name: "",
  email: "",
  phone: "",
  schoolType: "",
  grade: "",
  message: "",
  consent: false,
};

/** 어디서 보낸 신청인지. Lambda 가 이 값으로 검증 규칙과 보낼 Google Chat 스페이스를 고른다. */
export type ApplySource = "landing" | "trial_chapter";

/** Lambda 로 보내는 모든 폼의 공통 모양. `source` 만 있으면 나머지 필드는 폼마다 다르다. */
export type ApplySubmission = { source: ApplySource } & Record<string, unknown>;

export type ApplyPayload = {
  region: string;
  name: string;
  email: string;
  phone: string;
  schoolType: string;
  grade: string;
  message: string;
  source: ApplySource;
};

/**
 * 제출 직전 검증. 첫 번째로 걸린 항목의 안내 문구를 돌려주고, 다 통과하면 null.
 * 이메일 형식은 브라우저의 `<input type="email">` 검증(`checkValidity`)에 맡기므로
 * 그 결과를 `emailValid` 로 받는다.
 */
export const validateApplyForm = (
  form: ApplyForm,
  emailValid: boolean
): string | null => {
  if (!form.region) return "지역을 선택해 주세요.";
  if (!form.name.trim()) return "성함을 입력해 주세요.";
  if (!emailValid) return "이메일 주소를 다시 확인해 주세요.";
  const phone = form.phone.trim();
  if (!phone) return "전화번호를 입력해 주세요.";
  if (!PHONE.test(phone.replace(/\s/g, "")))
    return "전화번호를 다시 확인해 주세요. 예: 010-1234-5678";
  if (!form.schoolType) return "학교 구분을 선택해 주세요.";
  if (!form.grade) return "학년을 선택해 주세요.";
  if (!form.consent) return "개인정보 수집·이용에 동의해 주세요.";
  return null;
};

/** 필수 항목이 전부 찼는지. 제출 버튼을 "비활성처럼" 보이게 할 때 쓴다(실제 disabled 는 안 건다). */
export const isApplyFormComplete = (form: ApplyForm, emailValid: boolean): boolean =>
  !!(
    form.region &&
    form.name.trim() &&
    form.email.trim() &&
    emailValid &&
    form.phone.trim() &&
    PHONE.test(form.phone.replace(/\s/g, "")) &&
    form.schoolType &&
    form.grade &&
    form.consent
  );

export const buildApplyPayload = (form: ApplyForm, source: ApplySource): ApplyPayload => ({
  region: form.region,
  name: form.name.trim(),
  email: form.email.trim(),
  phone: form.phone.trim(),
  schoolType: form.schoolType,
  grade: form.grade,
  message: form.message.trim(),
  source,
});

export type ApplyResult = { ok: true } | { ok: false; error: string };

const SEND_FAILED = "전송에 실패했습니다. 잠시 후 다시 시도해 주세요.";

/**
 * Lambda 로 전송. 엔드포인트가 비어 있으면(설정 전) 전송 없이 성공으로 친다 — 화면 흐름은
 * 확인할 수 있게 두고 입력값은 콘솔에 남긴다.
 * 서버가 돌려준 안내 문구(`error`)가 있으면 그대로 보여 준다.
 */
export const submitApply = async (payload: ApplySubmission): Promise<ApplyResult> => {
  const endpoint = getLandingFormEndpoint();
  if (!endpoint) {
    console.warn("[apply] 신청 엔드포인트가 비어 있어 전송하지 않았습니다.", payload);
    return { ok: true };
  }
  try {
    const res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (res.ok) return { ok: true };
    const data = await res.json().catch(() => ({} as { error?: string }));
    return { ok: false, error: data.error || SEND_FAILED };
  } catch {
    return { ok: false, error: SEND_FAILED };
  }
};
